import { useCallback, useEffect, useMemo, useState } from "react";

import { twMerge } from "tailwind-merge";

import style from "./style.module.scss";

// ─── Step icons ────────────────────────────────────────────────────────────

function IconSearch() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="6.5" cy="6.5" r="4" stroke="currentColor" strokeWidth="1.4" />
      <line
        x1="9.5"
        y1="9.5"
        x2="13.5"
        y2="13.5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <line
        x1="4.5"
        y1="6.5"
        x2="8.5"
        y2="6.5"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
      <line
        x1="6.5"
        y1="4.5"
        x2="6.5"
        y2="8.5"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconNetwork() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="3" r="2" stroke="currentColor" strokeWidth="1.3" />
      <circle cx="2.5" cy="12" r="2" stroke="currentColor" strokeWidth="1.3" />
      <circle cx="13.5" cy="12" r="2" stroke="currentColor" strokeWidth="1.3" />
      <line
        x1="7"
        y1="4.7"
        x2="3.5"
        y2="10.2"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
      <line
        x1="9"
        y1="4.7"
        x2="12.5"
        y2="10.2"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
      <line
        x1="4.5"
        y1="12"
        x2="11.5"
        y2="12"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconClock() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.4" />
      <line
        x1="8"
        y1="5"
        x2="8"
        y2="8.5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <line
        x1="8"
        y1="8.5"
        x2="10.5"
        y2="10"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconChart() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect
        x="2"
        y="9"
        width="3"
        height="5"
        rx="1"
        fill="currentColor"
        opacity="0.6"
      />
      <rect
        x="6.5"
        y="6"
        width="3"
        height="8"
        rx="1"
        fill="currentColor"
        opacity="0.8"
      />
      <rect x="11" y="3" width="3" height="11" rx="1" fill="currentColor" />
    </svg>
  );
}

function IconDocument() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect
        x="3"
        y="2"
        width="10"
        height="12"
        rx="1.5"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      <line
        x1="5.5"
        y1="6"
        x2="10.5"
        y2="6"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
      <line
        x1="5.5"
        y1="8.5"
        x2="10.5"
        y2="8.5"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
      <line
        x1="5.5"
        y1="11"
        x2="8.5"
        y2="11"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconStamp() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect
        x="3"
        y="2"
        width="10"
        height="9"
        rx="1.5"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      <polyline
        points="5.5,6.5 7.2,8.2 10.5,5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="2"
        y1="13"
        x2="14"
        y2="13"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconSend() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M13.5 2.5L7 9"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <path
        d="M13.5 2.5L9 13.5L7 9L2.5 7L13.5 2.5Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <polyline
        points="2.5,7 5.5,10 11.5,4"
        stroke="#24a9a7"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.5"
      />
    </svg>
  );
}

// ─── Step definitions ──────────────────────────────────────────────────────

const titles_all = [
  {
    text: "Gathering intelligence on the company",
    Icon: IconSearch,
    duration: 2500,
  },
  {
    text: "Formulating background research on the company",
    Icon: IconNetwork,
    duration: 7500,
  },
  {
    text: "Understanding its management and roadmap",
    Icon: IconClock,
    duration: 7500,
  },
  {
    text: "Analyzing company's competitive landscape",
    Icon: IconChart,
    duration: 5000,
  },
  {
    text: "Synthesizing financials and valuations",
    Icon: IconDocument,
    duration: 2500,
  },
  {
    text: "Weighing risks against its outlook",
    Icon: IconStamp,
    duration: 2500,
  },
  {
    text: "Bringing it all together",
    Icon: IconSend,
    duration: 2500,
  },
  { text: "Finalizing the content", Icon: IconSearch, duration: 2500 },
  { text: "Getting it ready for you", Icon: IconNetwork, duration: 2500 },
];

// ─── Component ────────────────────────────────────────────────────────────

export default function PrimerRequestLoader({
  type = "peers",
}: {
  type?: "peers" | "transactions";
}) {
  const [step, setStep] = useState(0);

  const titles = useMemo(() => {
    if (type !== "peers") return titles_all;
    return titles_all.filter(
      (title) =>
        title.text !== "Looking up precedent transactions and their details"
    );
  }, [type]);

  const startTimer = useCallback(() => {
    const duration =
      type === "peers" ? titles[step].duration : titles[step].duration * 2;
    const interval = setInterval(() => {
      if (step < titles.length - 1) {
        setStep(step + 1);
      }
    }, duration);
    return interval;
  }, [step, titles, type]);

  useEffect(() => {
    const interval = startTimer();
    return () => clearInterval(interval);
  }, [startTimer]);

  const progressPct = ((step + 1) / titles.length) * 100;

  return (
    <div className="flex flex-col items-center justify-center gap-[28px] p-[40px]">
      {/* Orbit system */}
      <div className="relative w-[188px] h-[188px]">
        <div
          className="absolute rounded-full border border-dashed border-smart-teal/20"
          style={{
            width: 104,
            height: 104,
            top: "50%",
            left: "50%",
            margin: "-52px 0 0 -52px",
          }}
        />
        <div
          className="absolute rounded-full border border-dashed border-smart-teal/10"
          style={{
            width: 156,
            height: 156,
            top: "50%",
            left: "50%",
            margin: "-78px 0 0 -78px",
          }}
        />
        <div
          className="absolute rounded-full flex items-center justify-center z-[3] transition-all duration-500"
          style={{
            width: 66,
            height: 66,
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: "linear-gradient(135deg, #e6f7f7, #c8eeee)",
            border: "1.5px solid rgba(36,169,167,0.35)",
            boxShadow:
              "0 0 24px rgba(36,169,167,0.2), 0 2px 10px rgba(36,169,167,0.15)",
          }}
        >
          <div
            className="text-smart-teal"
            style={{
              width: 28,
              height: 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {(() => {
              const { Icon } = titles[step];
              return <Icon />;
            })()}
          </div>
        </div>
        <div className={style.orbitDot1} />
        <div className={style.orbitDot2} />
        <div className={style.orbitDot3} />
      </div>

      {/* Agentic progress */}
      <div className="w-full max-w-[700px] flex flex-col gap-[16px]">
        {/* Thin progress bar */}
        <div className="w-full h-[3px] bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-smart-teal rounded-full transition-all duration-700"
            style={{ width: `${progressPct}%` }}
          />
        </div>

        {/* Step list */}
        <div className="flex flex-col">
          {titles.map(({ text, Icon }, i) => {
            const isDone = i < step;
            const isActive = i === step;

            return (
              <div
                key={i}
                className="flex items-center gap-[12px] relative py-[10px]"
              >
                {/* Vertical connecting line */}
                <div className="relative">
                  {i < titles.length - 1 && (
                    <div
                      className="absolute left-[50%] top-[28px] w-[1px] bg-smart-teal/15"
                      style={{ height: "calc(100% - 8px)" }}
                    />
                  )}

                  {/* Step icon */}
                  <div
                    className={twMerge(
                      "flex-shrink-0 animate-pulse relative z-10 transition-all duration-300 rounded-full w-[28px] h-[28px] flex items-center justify-center",
                      isDone && "text-smart-teal bg-smart-teal/10",
                      isActive && "text-white bg-smart-teal",
                      !isDone && !isActive && "text-slate-300"
                    )}
                  >
                    <Icon />
                  </div>
                </div>

                {/* Label */}
                <span
                  className={twMerge(
                    "flex-1 text-[14px] transition-all duration-300",
                    isDone && "text-smart-teal/55",
                    isActive && "text-slate-700 font-medium",
                    !isDone && !isActive && "text-slate-300"
                  )}
                >
                  {text}
                  {isActive && <span className={style.animDots}> ...</span>}
                </span>

                {/* Check icon */}
                {(isDone || isActive) && <CheckIcon />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
